import { AnyAction } from 'redux';
import { Task } from 'redux-saga';
import deburr from 'lodash.deburr';
import { all, takeEvery, fork, put, select, call, take, race, delay, cancelled, cancel } from 'redux-saga/effects';
import { appConfigActions, appConfigActionTypes, appConfigActionsInterfaces } from '.';

import { getPdfFile, getProtocolData, getPairEffectsData, getRealizatorzyState, getCopySubjectModalData } from './selectors';
import { openNotificationAdmin } from '../../components/features/NotificationAdmin';
import { executeErrorCode } from '../auth/actions';
import { getUser, getUserId } from '../auth/selectors';
import AppConfig from '../../services/appConfig';
import formService, { CopyFormPayload } from '../../services/forms';
import Protocols from '../../services/protocols';
import SubjectsCards from '../../services/subjectsCards';
import utilityActions from '../utility/actions';
import { NotificationTypes } from '../utility/types';
import SubjectsCardsService from '../../services/subjectsCards';
import {
  init as kartaPrzedmiotuInit,
  setFormTemplateVersion as kartaPrzedmiotuSetFormTemplateVersion,
} from '../dynamicTabs/kartaPrzedmiotu/actions';
import urlBuilder from '../../helpers/urlBuilder';
import StorageManager from '../../helpers/storageManager';
import { getTabData } from '../dynamicTabs/selectors';

import { adminPanelUrl } from '../../settings';
import { localStorageLanguageKey } from '../../settings/i18n/languages';

const { showNotification } = utilityActions;

export function* generateSingleProtocolWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PROTOCOL_GENERATE_SINGLE_START, generateSingleProtocol);
}
/**
 * generowanie protokołu dla jednego przedmiotu
 *
 * @export
 * @param {GenerateSingleProtocol} action
 * @return {*}  {Generator}
 */
export function* generateSingleProtocol(action: appConfigActionsInterfaces.GenerateSingleSubjectProtocols): Generator {
  const abortController = new AbortController();
  try {
    const { cancel }: any = yield race({
      response: call(Protocols.generateSingleProtocol, action.payload, abortController.signal),
      cancel: take(appConfigActionTypes.PROTOCOL_MODAL_CLOSE),
    });
    if (cancel) {
      abortController.abort();
    } else {
      yield put(appConfigActions.generateSingleProtocolSuccess());
      const protocolData = {
        kodPrzedmiotu: [action.payload.kodPrzedmiotu.replaceAll('-', ' ')],
        poraRoku: action.payload.poraRoku,
      };
      yield put(appConfigActions.getProtocolStatus(protocolData));
    }
  } catch (error) {
    yield put(appConfigActions.generateSingleProtocolFail());
    if (error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}
export function* generateMultiSubjectsProtocolsWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PROTOCOL_GENERATE_GROUP_START, generateGroupProtocols);
}
/**
 * Generowanie protokołu dla kilku przedmiotów
 *
 * @export
 * @param {GenerateGroupProtocol} action
 * @return {*}  {Generator}
 */
export function* generateGroupProtocols(action: appConfigActionsInterfaces.GenerateGroupSubjectsProtocols): Generator {
  const abortController = new AbortController();
  try {
    const { cancel }: any = yield race({
      response: call(Protocols.generateGroupProtocols, action.payload, abortController.signal),
      cancel: take(appConfigActionTypes.PROTOCOL_MODAL_CLOSE),
    });
    if (cancel) {
      abortController.abort();
    } else {
      yield put(appConfigActions.generateGroupProtocolsSuccess());
      const kodPrzedmiotu = action.payload.protokoly.map(protocol => protocol.kodPrzedmiotu.replaceAll('-', ' '));
      const protocolData = {
        kodPrzedmiotu: kodPrzedmiotu,
        poraRoku: action.payload.poraRoku,
      };
      yield put(appConfigActions.getProtocolStatus(protocolData));
    }
  } catch (error) {
    yield put(appConfigActions.generateGroupProtocolsFail());
    if (error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}
export function* getProtocolStatusWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PROTOCOL_GET_STATUS_START, getProtocolStatus);
}
/**
 * Generowanie protokołu dla kilku przedmiotów
 *
 * @export
 * @param {GetProtocolStatus} action
 * @return {*}  {Generator}
 */
export function* getProtocolStatus(action: appConfigActionsInterfaces.GetProtocolStatus): Generator {
  try {
    const statusLoop = (yield call(getProtocolStatusLoop, action)) as Task;

    const actionTaken = (yield take([
      appConfigActionTypes.PROTOCOL_GET_STATUS_SUCCESS,
      appConfigActionTypes.PROTOCOL_MODAL_CLOSE,
    ])) as AnyAction;

    if (actionTaken.type === appConfigActionTypes.PROTOCOL_MODAL_CLOSE) {
      yield cancel(statusLoop);
    }
  } catch (error) {
    yield put(appConfigActions.getProtocolStatusFail());
    if (error.message) {
      const parsedMessage = Number.parseInt(error.message);
      yield put(executeErrorCode(parsedMessage, action, error));
    }
  }
}

function* getProtocolStatusLoop(action: appConfigActionsInterfaces.GetProtocolStatus): Generator {
  const abortController = new AbortController();
  let finished = false;

  try {
    while (!finished) {
      const response = (yield call(
        Protocols.getProtocolStatus,
        action.payload,
        abortController.signal,
      )) as appConfigActionsInterfaces.ProtocolStatusApiResponse;

      const finder = response.protokolyPageDto.data.find(prot => prot.czyWygenerowany === 0 || prot.czyWygenerowany === 1);

      if (!finder) {
        yield put(appConfigActions.getProtocolStatusSuccess(response.protokolyPageDto.data));
        finished = true;
      }
      yield delay(5000);
    }
  } finally {
    if (yield cancelled()) {
      finished = true;
      yield call([abortController, 'abort']);
    }
  }
}

export function* getPdfWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PROTOCOL_PDF_GET_START, getPdf);
}
/**
 * Pobieranie pliku PDF z serwera
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getPdf(action: appConfigActionsInterfaces.GetPdf): Generator {
  const abortController = new AbortController();
  const { protId } = action.payload;
  try {
    const { cancel, response }: any = yield race({
      response: call(Protocols.getProtocolPdf, { protId }, abortController.signal),
      cancel: take(appConfigActionTypes.PROTOCOL_MODAL_CLOSE),
    });
    if (cancel) {
      abortController.abort();
    } else {
      yield put(appConfigActions.getPdfSuccess(protId, response.data));
    }
  } catch (error) {
    yield put(appConfigActions.getPdfFail(protId));
    if (error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* printOutPdfWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PROTOCOL_PDF_PRINT_OUT_START, printOutPdf);
}
/**
 * Drukowanie pliku PDF
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* printOutPdf(action: appConfigActionsInterfaces.PrintOutProtocol): Generator {
  const { protId } = action.payload;
  yield call(getPdf, appConfigActions.getPdf(protId));
  const pdfBase64 = (yield select(state => getPdfFile(state, protId))) as Protocol.Status['pdfFile'];
  if (pdfBase64) {
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const file = new Blob([byteArray], { type: 'application/pdf;base64' });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
    yield put(appConfigActions.printOutProtocolSuccess(protId));
    URL.revokeObjectURL(fileURL);
  }
}
export function* downloadPdfWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PROTOCOL_PDF_DOWNLOAD_START, downloadPdf);
}
/**
 * Pobieranie pliku PDF na dysk
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* downloadPdf(action: appConfigActionsInterfaces.DownloadProtocol): Generator {
  const { protId } = action.payload;
  yield call(getPdf, appConfigActions.getPdf(protId));
  const pdfBase64 = (yield select(state => getPdfFile(state, protId))) as Protocol.Status['pdfFile'];
  const user = (yield select(getUser)) as AppConfig.User;
  if (pdfBase64) {
    const { kodPrzedmiotu, kodWydzialu, rokProtokolu, poraRokuProtokolu, numerIdKierownika } = (yield select(state =>
      getProtocolData(state, protId),
    )) as Protocol.Basic;
    // == występuje dlatego, ponieważ typeof user.id pokazuje jako string (chociaż ma typ number), a numer kierownika jest numberem, więc porównanie === nie pójdzie
    // dodatkowo żadne rzutowanie nie działa, ponieważ TS uważa, że to jest już numberem, a numerIdKierownika na string nie przejdzie, bo TS uzna, że jest ciągle false
    // (różnice w TS i runtime -> przychodzi jako string, a jest typowane jako number)
    const userProtocolInfo =
      user.id == numerIdKierownika ? `${deburr(user.lastName ?? '')}_${user.firstName?.substring(0, 1)}` : numerIdKierownika;
    const link = document.createElement('a');
    link.setAttribute('href', 'data:application/pdf;base64,' + pdfBase64);
    link.setAttribute(
      'download',
      `${userProtocolInfo}_${kodWydzialu}_${kodPrzedmiotu.replaceAll(' ', '')}_${rokProtokolu}_${poraRokuProtokolu}_${protId}`,
    );
    document.body.appendChild(link);
    link.click();
    if (link && link.parentNode) link.parentNode.removeChild(link);
    yield put(appConfigActions.downloadProtocolSuccess(protId));
  }
}

export function* getChangeLogWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.CHANGE_LOG_MODAL_OPEN, getChangeLog);
}
/**
 * Pobieranie historii ocen
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getChangeLog(action: appConfigActionsInterfaces.OpenChangeLogModal): Generator {
  const { kodPrzedmiotu, kartaPrzedmiotId } = action.payload;
  try {
    const requiredColumns = ['ocena', 'dataOceny', 'dataZmiany', 'nazwaFormyZajec', 'wpisujacyOceneNazwa', 'prowadzacyNazwa'];
    const requestParams = {
      kodPrzedmiotu: kodPrzedmiotu.replaceAll(' ', '-'),
      kartaPrzedmiotId,
      columns: requiredColumns,
      limit: '30',
    };
    const response = (yield call(AppConfig.getChangeLog, requestParams)) as appConfigActionsInterfaces.ChangeLogApiResponse;
    yield put(appConfigActions.getChangeLogSuccess(response.historiaOcenPage.data));
  } catch (error) {
    yield put(appConfigActions.getChangeLogFail());

    yield put(executeErrorCode(error.errorCode, action, error));
  }
}

export function* getStudyProgramsWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PAIR_EFFECTS_SELECT_CARD_VERSION, getStudyPrograms);
}
/**
 * Pobieranie programów studiów
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getStudyPrograms(action: appConfigActionsInterfaces.SelectCardVersion): Generator {
  try {
    const requiredStudyProgramColumns = ['kodProgramu', 'programStudiowId', 'nazwaProgramu', 'kodMen'];
    const { selectedVersion, data } = (yield select(getPairEffectsData)) as Modal.PairEffects;
    yield put(appConfigActions.getStudyPrograms());
    if (data) {
      const requestParams = {
        filter: urlBuilder.generateFiltersArray({ przedmiotId: data.przedmiotId, wersjaKarty: selectedVersion }),
        columns: requiredStudyProgramColumns,
        limit: '100',
      };
      const response: any = yield call(SubjectsCards.getStudyPrograms, requestParams);
      yield put(appConfigActions.getStudyProgramsSuccess(response.ztnProgramyStudiowListaViewPage.data));
    }
  } catch (error) {
    yield put(appConfigActions.getStudyProgramsFail());
    yield put(executeErrorCode(error.errorCode, action, error));
  }
}

export function* refreshLearningEffectsWatcher(): Generator {
  yield takeEvery(
    [appConfigActionTypes.PAIR_EFFECTS_SELECT_CARD_LANGUAGE, appConfigActionTypes.PAIR_EFFECTS_SELECT_STUDY_PROGRAM],
    refreshLearningEffects,
  );
}
/**
 * odświeżenie listy efektów kierunkowych po zmianie parametrów
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* refreshLearningEffects(
  action:
    | appConfigActionsInterfaces.SelectCardVersion
    | appConfigActionsInterfaces.SelectCardLanguage
    | appConfigActionsInterfaces.SelectStudyProgram,
): Generator {
  const { selectedStudyProgram, selectedLang, selectedVersion } = (yield select(getPairEffectsData)) as Modal.PairEffects;
  if (selectedStudyProgram && selectedLang && selectedVersion) {
    yield put(appConfigActions.getLearningEffects());
  }
}
export function* getLearningEffectsWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PAIR_EFFECTS_GET_LEARNING_EFFECTS_START, getLearningEffects);
}
/**
 * pobieranie efektów kierunkowych
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getLearningEffects(action: appConfigActionsInterfaces.GetLearningEffects): Generator {
  try {
    const requiredColumns = ['efektKierunkowyId', 'efektKierunkowyKod', 'kategoriaKod', 'opisEfektuKierunkowego'];
    const { selectedStudyProgram, selectedLang, selectedVersion, data } = (yield select(getPairEffectsData)) as Modal.PairEffects;
    if (data) {
      const requestParams = {
        filter: urlBuilder.generateFiltersArray({
          przedmiotId: data.przedmiotId,
          wersjaKarty: selectedVersion,
          jezykKarty: selectedLang,
          programStudiowId: selectedStudyProgram,
        }),
        columns: requiredColumns,
      };
      const response: any = yield call(SubjectsCards.getLearningEffects, requestParams);

      yield put(appConfigActions.getLearningEffectsSuccess(response.ztnEfektyKierunkoweListaViewPage.data));
      yield put(appConfigActions.getAlreadyPairedEffects());
    }
  } catch (error) {
    yield put(appConfigActions.getLearningEffectsFail());
    yield put(executeErrorCode(error.errorCode, action, error));
  }
}
export function* getAlreadyPairedEffectsWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PAIR_EFFECTS_GET_ALREADY_PAIRED_EFFECTS_START, getAlreadyPairedEffects);
}
/**
 * Pobieranie już sparowanych efektów kierunkowych
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getAlreadyPairedEffects(action: appConfigActionsInterfaces.GetAlreadyPairedEffects): Generator {
  try {
    const requiredColumns = [
      'efektKierunkowyId',
      'efektKierunkowyKod',
      'kategoriaKod',
      'opisEfektuKierunkowego',
      'parowanieEfektuKierId',
    ];
    const { selectedStudyProgram, selectedLang, selectedVersion, data } = (yield select(getPairEffectsData)) as Modal.PairEffects;
    if (data) {
      const requestParams = {
        filter: urlBuilder.generateFiltersArray({
          przedmiotId: data.przedmiotId,
          wersjaKarty: selectedVersion,
          jezykKarty: selectedLang,
          programStudiowId: selectedStudyProgram,
        }),
        columns: requiredColumns,
      };
      const response: any = yield call(SubjectsCards.getAlreadyPairedEffects, requestParams);
      yield put(
        appConfigActions.getAlreadyPairedEffectsSuccess(
          response.ztnEfektyKierunkoweListaViewPageWithDate.data.data.map(
            (item: AppConfig.LearningEffect) => item.efektKierunkuId,
          ),
        ),
      );
    }
  } catch (error) {
    yield put(appConfigActions.getAlreadyPairedEffectsFail());
    yield put(executeErrorCode(error.errorCode, action, error));
  }
}
export function* saveEffectsToTemporaryTableWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.PAIR_EFFECTS_SAVE_START, saveEffectsToTemporaryTable);
}
/**
 * Zapisywanie efektów kierunkowych to tabeli tymczasowej
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* saveEffectsToTemporaryTable(action: appConfigActionsInterfaces.SavePairedEffects): Generator {
  try {
    const { selectedEffects, data, selectedVersion, selectedLang, selectedStudyProgram } = (yield select(
      getPairEffectsData,
    )) as Modal.PairEffects;
    const requestAttributes = {
      przedmiotId: data?.przedmiotId,
      jezykKarty: selectedLang,
      wersjaSzablonuKarty: selectedVersion,
      programStudiowId: selectedStudyProgram,
    };
    yield call(SubjectsCards.saveEffectsInTemporaryTable, {
      ...requestAttributes,
      daneKarty: selectedEffects,
      typDokumentu: 'efekt_kierunkowy',
    });
    yield put(appConfigActions.closePairEffectsModal());
    yield put(
      showNotification({
        notifType: 'success' as NotificationTypes,
        titleTranslationKey: 'PAIR_EFFECTS_SUCCESS_TITLE',
        bodyTranslationKey: 'PAIR_EFFECTS_SUCCESS_DESCRIPTION',
      }),
    );
  } catch (error) {
    yield put(appConfigActions.savePairedEffectsFail());
    yield put(executeErrorCode(error.errorCode, action, error));
  }
}
export function* getRealizatorzyListWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.REALIZATORZY_MODAL_OPEN, getRealizatorzyList);
}
/**
 * Pobieranie listy nauczycieli do modala realizatorów przedmiotu
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getRealizatorzyList(action: appConfigActionsInterfaces.OpenRealizatorzyModal): Generator {
  try {
    const { realizatorzyList } = (yield select(getRealizatorzyState)) as Modal.Realizatorzy;
    if (!realizatorzyList.length) {
      const res: any = yield call(AppConfig.getInstructors, {});
      const limit = res.pracownikPage.totalRecords.toString();
      const response: any = yield call(AppConfig.getInstructors, { limit });
      yield put(appConfigActions.getRealizatorzyListSuccess(response.pracownikPage.data));
    }
  } catch (error) {
    yield put(appConfigActions.getRealizatorzyListFail());
    yield put(executeErrorCode(error.errorCode, action, error));
  }
}
export function* setSearchPhraseCopyDataModalWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.COPY_SUBJECT_CARD_SET_SEARCH, setSearchPhraseCopyDataModal);
}
/**
 * Inizjalizacja pobierania listy kart przedmiotów do modala kopiowania karty przedmiotu po wpisaniu szukanej karty przedmiotu
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* setSearchPhraseCopyDataModal(action: appConfigActionsInterfaces.SetCopyCardsSearch): Generator {
  yield put(appConfigActions.getSubjectsCardsListToModal());
}

export function* getSubjectsCardsListWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.COPY_SUBJECT_CARD_GET_LIST_START, getSubjectsCardsList);
}
/**
 *  pobieranie listy kart przedmiotów do select'a kopiowania kart
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getSubjectsCardsList(action: appConfigActionsInterfaces.GetSubjectsCardsListToModal): Generator {
  try {
    const { searchPhrase } = (yield select(getCopySubjectModalData)) as Modal.CopySubjectCard;
    const columns: string[] = ['przedmiotNazwa', 'przedmiotId', 'jezykKarty', 'przedmiotKod'];
    const requestAtributes = {
      page: '1',
      columns,
      limit: '300',
      filter: searchPhrase ? { search: searchPhrase } : null,
    };
    const response: any = yield call(SubjectsCardsService.getSubjectsCards, requestAtributes);
    yield put(appConfigActions.getSubjectsCardsListToModalSuccess(response.kartaPrezdmiotuListaElementPageDto.data));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(appConfigActions.getSubjectsCardsListToModalFail());
  }
}

export function* copyDataFromCardWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.COPY_SUBJECT_CARD_START, copyDataFromCard);
}
/**
 * Kopiowanie danych z wybranej karty przedmiotu do aktualnie otwartej karty
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* copyDataFromCard(action: appConfigActionsInterfaces.CopySubjectCard): Generator {
  try {
    const { selected, przedmiotId, tabId } = (yield select(getCopySubjectModalData)) as Modal.CopySubjectCard;
    if (tabId) {
      const tab = (yield select(state => getTabData(state, tabId))) as DynamicTabWithForm<SubjectCardData>;
      if (selected && przedmiotId) {
        const requestAtributes: CopyFormPayload = {
          przedmiotIdSrc: selected,
          przedmiotIdDsc: przedmiotId,
        };
        yield call(formService.copyForm, requestAtributes);
        yield put(appConfigActions.copySubjectCardSuccess());
        yield put(
          showNotification({
            notifType: 'success' as NotificationTypes,
            titleTranslationKey: 'COPY_CARD_SUCCESS_TITLE',
            bodyTranslationKey: 'COPY_CARD_SUCCESS_DESCRIPTION',
          }),
        );
        yield put(appConfigActions.closeCopySubjectCardModal());
        // odświeżenie danych formularza po skopiowaniu danych z innej karty
        yield put(kartaPrzedmiotuInit(tabId));
        if (tab.form.templateVersion) {
          yield put(kartaPrzedmiotuSetFormTemplateVersion(tabId, tab.form.templateVersion));
        }
      } else {
        throw new Error();
      }
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(appConfigActions.copySubjectCardFail());
  }
}

export function* getStudentsToCopyFinalMarkModalWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.COPY_FINAL_MARKS_MODAL_GET_STUDENTS, getStudentsToCopyFinalMarkModal);
}
/**
 * ustawienie wybranych studentów oraz szczegołów zakładki w modalu przepisywania ocen ko ncowych na podstawie wyliczonych
 * Akcja dla zakładki strona-przedmiotu.
 *
 * @export
 * @param {AnyAction} { payload }
 * @return {*}  {Generator}
 */
export function* getStudentsToCopyFinalMarkModal({
  payload,
}: appConfigActionsInterfaces.GetStudentsToCopyFinalMarkModal): Generator {
  const { tabId } = payload;
  const userId = (yield select(getUserId)) as AppConfig.User['id'];
  const { table, leftPanel } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
  const { selected, data } = table;
  const { rokAkademicki, semestrAkademicki, przedmiotKod, wydzialZlecajacyKod, przedmiotId, typPrzydzialu } = leftPanel.data;
  let studentsWithoutOcenaWyliczona = 0;
  const studentsWithOcenaWyliczona = data
    .filter(student => selected.includes(student.studentId))
    .filter(student => {
      if (student.ocenaWyliczona) return student;
      else {
        studentsWithoutOcenaWyliczona++;
        return false;
      }
    });
  const studentsWithoutManuallySignedFinalMarks = studentsWithOcenaWyliczona.filter(
    student => student.czyOcenaReczna.toLowerCase() === 'nie',
  );
  let studentsWithMarksByAnotherCounter = 0;
  studentsWithoutManuallySignedFinalMarks.forEach(student => {
    if (student.ocenaKoncowa && student.wpisujacyOceneKoncowaId != userId) {
      studentsWithMarksByAnotherCounter++;
    }
  });
  if (studentsWithoutManuallySignedFinalMarks.length) {
    yield put(
      appConfigActions.setStudentsInCopyFinalMarkModal(
        studentsWithoutManuallySignedFinalMarks,
        selected.length,
        studentsWithoutOcenaWyliczona,
        studentsWithOcenaWyliczona.length - studentsWithoutManuallySignedFinalMarks.length,
        studentsWithMarksByAnotherCounter,
      ),
    );
    yield put(appConfigActions.openCopyFinalMarksModal());
    yield put(
      appConfigActions.setPrzedmiotInCopyFinalMarkModal(tabId, {
        rokAkademicki,
        semestrAkademicki,
        przedmiotKod,
        wydzialZlecajacyKod,
        przedmiotId,
        typPrzydzialu,
      }),
    );
  } else {
    yield put(
      showNotification({
        notifType: 'error' as NotificationTypes,
        titleTranslationKey: 'COPY_FINAL_MARKS_NO_CALCULATED_MARKS_TITLE',
        bodyTranslationKey: 'COPY_FINAL_MARKS_NO_CALCULATED_MARKS_DESCRIPTION',
      }),
    );
  }
}

export function* setLanguageWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.LANGUAGE_SET, setLanguage);
}
/**
 * ustawienie języka aplikacji w store
 *
 * @export
 * @param {LanguageActionTypes} { payload }
 * @return {*}  {Generator}
 */
export function* setLanguage({ payload }: appConfigActionsInterfaces.SetLanguage): Generator {
  const { language } = payload;
  yield (document.documentElement.lang = language);
  yield StorageManager.set(localStorageLanguageKey, language, true);
}

export function* redirectToAdminPanelWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.REDIRECT_TO_ADMIN_PANEL, redirectToAdminPanel);
}
/**
 * otwarcie okna z panelem admina
 *
 * @export
 * @return {*}  {Generator}
 */
export function* redirectToAdminPanel(): Generator {
  yield call([window, 'open'], adminPanelUrl, 'blank');
}

export function* checkApiStatusWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.CHECK_API_STATUS, checkApiStatus);
}
/**
 * sprawdzenie statusu api, zrealizowane w celu sprawdzenia czy maintenance mode jest włączony, nie potrzebna jest odpowiedz, ponieważ
 * otrzymamy interesujący kod błędu
 *
 * @export
 * @return {*}  {Generator}
 */
export function* checkApiStatus(action: appConfigActionsInterfaces.CheckApiStatus): Generator {
  try {
    yield call(AppConfig.checkApiStatus);
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  } finally {
    yield put(appConfigActions.checkApiStatusFinish());
  }
}

export function* getNotificationListAdminWatcher(): Generator {
  yield takeEvery(appConfigActionTypes.GET_NOTIFICATION_LIST_ADMIN, getNotificationListAdmin);
}
/**
 * wysłanie i obłużenie zapytania o listę powiadomień zwracanych z panelu admina
 *
 * @export
 * @return {*}  {Generator}
 */
export function* getNotificationListAdmin(action: appConfigActionsInterfaces.GetNotificationListAdmin): Generator {
  const { userId } = action.payload;
  try {
    const { notificationDtoList } = (yield call(
      AppConfig.getNotificationListAdmin,
    )) as appConfigActionsInterfaces.NotificationListAdminApiResponse;
    yield put(appConfigActions.getNotificationListAdminFinish(notificationDtoList));
    openNotificationAdmin(notificationDtoList, userId);
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export default function* rootSaga(): Generator {
  yield all([
    fork(generateSingleProtocolWatcher),
    fork(generateMultiSubjectsProtocolsWatcher),
    fork(getProtocolStatusWatcher),
    fork(getPdfWatcher),
    fork(printOutPdfWatcher),
    fork(downloadPdfWatcher),
    fork(getChangeLogWatcher),
    fork(refreshLearningEffectsWatcher),
    fork(saveEffectsToTemporaryTableWatcher),
    fork(getLearningEffectsWatcher),
    fork(getAlreadyPairedEffectsWatcher),
    fork(getStudyProgramsWatcher),
    fork(getRealizatorzyListWatcher),
    fork(getSubjectsCardsListWatcher),
    fork(setSearchPhraseCopyDataModalWatcher),
    fork(copyDataFromCardWatcher),
    fork(getStudentsToCopyFinalMarkModalWatcher),
    fork(setLanguageWatcher),
    fork(redirectToAdminPanelWatcher),
    fork(checkApiStatusWatcher),
    fork(getNotificationListAdminWatcher),
  ]);
}
